<template>
  <v-container fluid class="bi-parameter-rules-filter bi-compact-fields pr-5 py-2 pl-0" style="background-color: var(--v-background-base) !important;">
    <ExpansionPanel
      :always-visible-values="alwaysVisibleFiltersKey"
      :auto-search-on-always-visible-change="false"
      :show-expand-icon="false"
      :start-expanded="true"
      @on-clear="clearFilters"
      @on-search="searchData"
    >
      <template #always-visible>
        <v-col cols="12" sm="6" md="3">
          <PaginatedAutocompleteMulti
            :value="filter.carrierIds"
            :fetch="fetchCarriers"
            :refresh-list-items="refreshCarriers"
            :group-by-header="false"
            item-text="name"
            item-value="id"
            :label="$t('biControlPanel.parameterRules.filters.carrier')"
            :no-data-text="$t('biControlPanel.filters.noData')"
            :end-text="$t('biControlPanel.filters.endText')"
            :min-search-chars="0"
            :page-size="20"
            hide-details
            v-bind="autocompleteAttrs"
            @input="setFilter('carrierIds', $event)"
            @listUpdated="refreshCarriers = false"
            @error="handleFilterOptionsError"
          />
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <Autocomplete
            :value="filter.paymentModels"
            :items="paymentModelOptions"
            item-text="label"
            item-value="code"
            multiple
            :label="$t('biControlPanel.parameterRules.filters.paymentModel')"
            hide-details
            v-bind="autocompleteAttrs"
            @change="setFilter('paymentModels', $event)"
          >
            <template #selection="{ item, index }">
              <v-chip
                v-if="index === 0"
                small
                close
                color="primary"
                class="bi-compact-chip"
                :class="{ 'bi-compact-chip--grouped': filter.paymentModels.length > 1 }"
                @click:close="setFilter('paymentModels', filter.paymentModels.filter((code) => code !== item.code))"
              >
                <span class="bi-compact-chip__text">{{ item.label }}</span>
              </v-chip>
              <v-chip
                v-else-if="index === 1"
                small
                close
                color="primary"
                @click:close="setFilter('paymentModels', filter.paymentModels.slice(0, 1))"
              >
                +{{ filter.paymentModels.length - 1 }}
              </v-chip>
            </template>
          </Autocomplete>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <PaginatedAutocomplete
            :value="filter.financialGroupId"
            :fetch="fetchFinancialGroups"
            :refresh-list-items="refreshFinancialGroups"
            item-text="name"
            item-value="id"
            :label="$t('biControlPanel.parameterRules.filters.financialGroup')"
            :no-data-text="$t('biControlPanel.filters.noData')"
            :end-text="$t('biControlPanel.filters.endText')"
            :min-search-chars="0"
            :page-size="20"
            hide-details
            v-bind="autocompleteAttrs"
            @input="setFilter('financialGroupId', $event)"
            @listUpdated="refreshFinancialGroups = false"
            @error="handleFilterOptionsError"
          />
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <PaginatedAutocompleteMulti
            :value="filter.contractIds"
            :fetch="fetchContracts"
            :refresh-list-items="refreshContracts"
            :group-by-header="false"
            item-text="name"
            item-value="id"
            :label="$t('biControlPanel.parameterRules.filters.contract')"
            :no-data-text="$t('biControlPanel.filters.noData')"
            :end-text="$t('biControlPanel.filters.endText')"
            :enabled="!!filter.financialGroupId"
            :disabled="!filter.financialGroupId"
            :min-search-chars="0"
            :page-size="10"
            hide-details
            v-bind="autocompleteAttrs"
            @input="setFilter('contractIds', $event)"
            @listUpdated="refreshContracts = false"
            @error="handleFilterOptionsError"
          />
        </v-col>
      </template>

      <template #expanded>
        <v-col cols="12" sm="6" md="3">
          <Autocomplete
            :value="statusCode"
            :items="statusOptions"
            item-text="label"
            item-value="code"
            clearable
            :label="$t('biControlPanel.parameterRules.filters.status')"
            hide-details
            v-bind="autocompleteAttrs"
            @change="setStatus"
          />
        </v-col>
      </template>
    </ExpansionPanel>
    <SnackbarCustomize ref="SnackbarCustomize" />
  </v-container>
</template>

<script>
import ExpansionPanel from '@/components/BIControlPanel/ExpansionPanel.vue';
import SnackbarCustomize from '@/components/CustomAlerts/SnackbarCustomize.vue';
import PaginatedAutocomplete from '@/components/Default/PaginatedAutocomplete.vue';
import PaginatedAutocompleteMulti from '@/components/Default/PaginatedAutocompleteMulti.vue';
import Autocomplete from '@/components/Default/Autocomplete.vue';
import { AUTOCOMPLETE_ATTRS } from '@/shared/constants/admissionMap/DefaultConstants';
import BiPanelRuleModule from '@/store/modules/bi-panel-rule/bi-panel-rule-module';
import { BI_PAYMENT_MODEL_OPTIONS, BI_RULE_STATUS_OPTIONS } from '@/store/modules/bi-panel-rule/bi-panel-rule-factory';

export default {
  name: 'BIParameterRulesFilter',
  components: {
    ExpansionPanel,
    SnackbarCustomize,
    PaginatedAutocomplete,
    PaginatedAutocompleteMulti,
    Autocomplete,
  },
  data() {
    return {
      refreshFinancialGroups: false,
      refreshCarriers: false,
      refreshContracts: false,
      paymentModelOptions: BI_PAYMENT_MODEL_OPTIONS,
      statusOptions: BI_RULE_STATUS_OPTIONS,
    };
  },
  computed: {
    filter() {
      return BiPanelRuleModule.filter;
    },
    alwaysVisibleFiltersKey() {
      return [
        (this.filter.carrierIds || []).join(','),
        (this.filter.paymentModels || []).join(','),
        this.filter.financialGroupId || '',
        (this.filter.contractIds || []).join(','),
      ].join('|');
    },
    statusCode() {
      if (this.filter.active === null || this.filter.active === undefined) return null;

      return this.filter.active ? 'ACTIVE' : 'INACTIVE';
    },
    autocompleteAttrs() {
      return {
        ...AUTOCOMPLETE_ATTRS,
        borderSelected: false,
        placeholder: this.translate('biControlPanel.filters.placeholder'),
      };
    },
  },
  methods: {
    emptyFilterOptionsResponse() {
      return { content: [], last: true };
    },
    fetchFinancialGroups(page, size, searchText) {
      return this.fetchPaginatedFilterOptions('financialGroup', page, size, searchText);
    },
    fetchCarriers(page, size, searchText) {
      return this.fetchPaginatedFilterOptions('carrier', page, size, searchText);
    },
    fetchContracts(page, size, searchText) {
      if (!this.filter.financialGroupId) {
        return Promise.resolve(this.emptyFilterOptionsResponse());
      }

      return this.fetchPaginatedFilterOptions('contract', page, size, searchText);
    },
    async fetchPaginatedFilterOptions(fieldKey, page, size, searchText) {
      const result = await this.runStoreAction(
        BiPanelRuleModule.searchFilterOptions({
          fieldKey,
          reset: page === 0,
          page,
          size,
          searchText: searchText || '',
        }),
        this.translate('biControlPanel.parameterRules.messages.filterOptionsLoadError'),
      );

      if (!result || result.success === false || !result.data) {
        return this.emptyFilterOptionsResponse();
      }

      return result.data;
    },
    setFilter(fieldKey, value) {
      const normalizedValue = Array.isArray(BiPanelRuleModule.filter[fieldKey]) ? (value || []) : (value || null);
      const previousValue = this.filter[fieldKey];

      BiPanelRuleModule.setFilterValue({ fieldKey, value: normalizedValue });

      if (JSON.stringify(previousValue) === JSON.stringify(normalizedValue)) return;

      if (fieldKey === 'financialGroupId') {
        this.triggerListRefresh('refreshContracts');
      }
    },
    setStatus(code) {
      BiPanelRuleModule.setFilterValue({ fieldKey: 'active', value: code ? code === 'ACTIVE' : null });
    },
    triggerListRefresh(flagName) {
      this[flagName] = false;
      this.$nextTick(() => {
        this[flagName] = true;
      });
    },
    handleFilterOptionsError() {
      this.showError(this.translate('biControlPanel.parameterRules.messages.filterOptionsLoadError'));
    },
    async clearFilters() {
      const clearResult = await this.runStoreAction(
        BiPanelRuleModule.clearFilters(),
        this.translate('biControlPanel.parameterRules.messages.filtersClearError'),
      );

      if (!clearResult || clearResult.success === false) return;

      ['refreshFinancialGroups', 'refreshCarriers', 'refreshContracts'].forEach(
        (flagName) => this.triggerListRefresh(flagName),
      );
    },
    async searchData() {
      await this.runStoreAction(
        BiPanelRuleModule.submitFilters(),
        this.translate('biControlPanel.parameterRules.messages.searchError'),
      );
    },
    translate(key) {
      return this.$t(key).toString();
    },
    showError(message) {
      this.$refs.SnackbarCustomize.open('error', message);
    },
    async runStoreAction(actionPromise, fallbackMessage) {
      const result = await actionPromise;

      if (result && result.success === false) {
        this.showError(result.errorMessage || fallbackMessage);
      }

      return result;
    },
  },
};
</script>

<style lang="scss">
@import "@/shared/styles/biControlPanel/compactFields.scss";
</style>
